import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Trash2 } from "lucide-react";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export default function PainterAreas() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ postcode: "", suburb: "", state: "QLD", max_travel_km: "" });

  const load = async () => {
    try { const r = await api.get("/painter/areas"); setItems(r.data.items || []); }
    catch (e) {
      if (e?.response?.status === 404) { setItems([]); return; }
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.postcode || !form.suburb) { toast.error("Postcode and suburb required"); return; }
    try {
      await api.post("/painter/areas", { ...form, max_travel_km: form.max_travel_km ? Number(form.max_travel_km) : null });
      setForm({ postcode: "", suburb: "", state: form.state, max_travel_km: "" });
      await load(); toast.success("Service area added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/painter/areas/${id}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="painter-areas" className="space-y-8">
      <div>
        <div className="overline mb-2">Service areas</div>
        <h1 className="font-serif text-3xl text-brand-ink mb-1">Where you work</h1>
        <p className="text-sm text-brand-muted">Homeowner searches use postcode. Add every postcode you&rsquo;ll travel to.</p>
      </div>

      <div className="border border-brand-linen p-5 bg-white grid sm:grid-cols-4 gap-3" data-testid="area-form">
        <input data-testid="area-postcode" placeholder="Postcode" value={form.postcode}
          onChange={(e) => setForm({ ...form, postcode: e.target.value })}
          className="border border-brand-linen p-3 text-sm" />
        <input data-testid="area-suburb" placeholder="Suburb" value={form.suburb}
          onChange={(e) => setForm({ ...form, suburb: e.target.value })}
          className="border border-brand-linen p-3 text-sm sm:col-span-2" />
        <select data-testid="area-state" value={form.state}
          onChange={(e) => setForm({ ...form, state: e.target.value })}
          className="border border-brand-linen p-3 text-sm">
          {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={add} data-testid="area-add"
          className="sm:col-span-4 px-5 py-2.5 bg-brand-navy text-white text-sm">
          Add service area
        </button>
      </div>

      <ul className="divide-y border border-brand-linen bg-white" data-testid="area-list">
        {items.length === 0 && <li className="p-4 text-sm text-brand-muted">No service areas yet.</li>}
        {items.map((a) => (
          <li key={a.id} className="flex items-center justify-between p-4" data-testid={`area-${a.id}`}>
            <div>
              <div className="text-sm text-brand-ink">{a.postcode} · {a.suburb}, {a.state}</div>
              {a.max_travel_km && <div className="text-xs text-brand-muted">Up to {a.max_travel_km} km</div>}
            </div>
            <button onClick={() => remove(a.id)} data-testid={`area-remove-${a.id}`}
              className="p-1.5 text-brand-muted hover:text-red-600">
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
